import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { useCallback, useMemo, useState } from "react";
import type { Selection } from "react-aria-components";
import { useFilter } from "react-aria-components/Autocomplete";
import type { AttributeOption } from "#shared/reviewee-filters/filterOptionsContract.ts";
import { ListBox, ListBoxItem } from "@/components/ui/list-box.tsx";
import { Note } from "@/components/ui/note.tsx";
import { SearchField, SearchInput } from "@/components/ui/search-field.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Text } from "@/components/ui/text.tsx";
import { FilterPanelBody } from "./FilterPanelBody.tsx";
import { FilterPanelHeader } from "./FilterPanelHeader.tsx";

const PLACEHOLDER_OPTIONS: AttributeOption[] = [
    { value: "skeleton-1", label: "Xxxxxxxxx xxxx" },
    { value: "skeleton-2", label: "Xxxxx xxxxxxxxx" },
    { value: "skeleton-3", label: "Xxxxxxxxxxx" },
    { value: "skeleton-4", label: "Xxxxxxx xxxxx" }
];

export interface AttributeFilterPanelLabels {
    title: string;
    back: string;
    searchPlaceholder: string;
    loading: string;
    empty: string;
    noMatches: string;
    error: string;
    getResultsAnnouncement: (count: number) => string;
}

export interface AttributeFilterPanelProps {
    labels: AttributeFilterPanelLabels;
    options: AttributeOption[];
    isPending: boolean;
    isError: boolean;
    selectedValues: string[];
    onSelectionChange: (values: string[]) => void;
    onBack: () => void;
    maxSelections?: number;
}

export function AttributeFilterPanel({ labels, options, isPending, isError, selectedValues, onSelectionChange, onBack, maxSelections }: AttributeFilterPanelProps) {
    const [query, setQuery] = useState("");
    const [valuesSelectedOnOpen] = useState(() => new Set(selectedValues));
    const { contains } = useFilter({ sensitivity: "base" });

    const { items, hasContent, isLoading, isEmpty } = useSkeletonState({ isPending, data: options, placeholder: PLACEHOLDER_OPTIONS });

    const matches = useMemo(() => options.filter(option => contains(option.label, query)), [options, query, contains]);

    const isNoMatches = !isLoading && !isEmpty && matches.length === 0;
    const isListVisible = hasContent && !isEmpty && !isNoMatches;

    const isAtCap = maxSelections !== undefined && selectedValues.length >= maxSelections;

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            const next = keys === "all" ? options.map(option => option.value) : [...keys].map(String);

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [options, onSelectionChange, maxSelections]
    );

    const rows = useMemo(() => {
        if (isLoading) {
            return items;
        }

        const selectedRows = matches.filter(option => valuesSelectedOnOpen.has(option.value));
        const unselectedRows = matches.filter(option => !valuesSelectedOnOpen.has(option.value));

        return [...selectedRows, ...unselectedRows];
    }, [isLoading, items, matches, valuesSelectedOnOpen]);

    return (
        <FilterPanelBody className="w-72">
            <FilterPanelHeader title={labels.title} onBack={onBack} backLabel={labels.back} />
            <SearchField aria-label={labels.searchPlaceholder} value={query} onChange={setQuery}>
                <SearchInput placeholder={labels.searchPlaceholder} />
            </SearchField>
            <output className="sr-only">{isLoading ? labels.loading : labels.getResultsAnnouncement(matches.length)}</output>
            {isError ? (
                <Note intent="danger" role="alert">
                    {labels.error}
                </Note>
            ) : (
                <>
                    {isListVisible && (
                        <Skeleton isLoading={isLoading} inert={isLoading} className="flex min-h-0 shrink flex-col">
                            <ListBox aria-label={labels.title} selectionMode="multiple" selectedKeys={selectedValues} onSelectionChange={handleSelectionChange} escapeKeyBehavior="none" items={rows} className="max-h-64 min-h-0 border-0">
                                {option => (
                                    <ListBoxItem id={option.value} textValue={option.label} isDisabled={isAtCap && !selectedValues.includes(option.value)}>
                                        {option.label}
                                    </ListBoxItem>
                                )}
                            </ListBox>
                        </Skeleton>
                    )}
                    {isEmpty && <Text>{labels.empty}</Text>}
                    {isNoMatches && <Text>{labels.noMatches}</Text>}
                </>
            )}
        </FilterPanelBody>
    );
}
